import { z } from 'zod';

/**
 * The in-app change log ("What's new"): one entry per user-visible change,
 * written by hand in `apps/api/changelog/changelog.json` in the pull request
 * that makes the change, newest first. The file ships inside the API's deploy
 * and nowhere else, so an entry is visible exactly when that deploy is live.
 *
 * Imports zod and nothing else. `scripts/gate-changelog.mjs` loads this file
 * straight from source through Node's type stripping, which does not map a
 * `.js` specifier onto a `.ts` file, so the slug and date primitives are
 * restated here rather than imported from `./primitives.js`.
 */

export const CHANGELOG_AUDIENCES = ['everyone', 'coaches', 'parents'] as const;
export const ChangelogAudienceSchema = z.enum(CHANGELOG_AUDIENCES);
export type ChangelogAudience = z.infer<typeof ChangelogAudienceSchema>;

export const CHANGELOG_TITLE_MAX = 80;
/** One to three plain sentences. */
export const CHANGELOG_SUMMARY_MAX = 300;

/** Rendered as text: no tags, no markdown markers, no em dash, one line. */
const NOT_PLAIN = /[<>[\]*_`\u2014\r\n]/;
const PLAIN_TEXT_MESSAGE = 'plain text only: no tags, no markdown, no em dash, one line';

const plainText = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    .refine((text) => !NOT_PLAIN.test(text), PLAIN_TEXT_MESSAGE);

export const ChangelogEntrySchema = z.strictObject({
  id: z
    .string()
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'a kebab-case slug'),
  date: z.iso.date(),
  title: plainText(CHANGELOG_TITLE_MAX),
  summary: plainText(CHANGELOG_SUMMARY_MAX),
  audience: ChangelogAudienceSchema,
});
export type ChangelogEntry = z.infer<typeof ChangelogEntrySchema>;

export const ChangelogSchema = z.array(ChangelogEntrySchema);

export interface ChangelogCheck {
  readonly entries: readonly ChangelogEntry[];
  /** Empty when the file is fit to ship. */
  readonly problems: readonly string[];
}

/**
 * Everything the gate and the screen agree on: the shape, unique ids, newest
 * first, and no date after `today` (YYYY-MM-DD, supplied by the caller because
 * this package reads no clock).
 */
export function checkChangelog(value: unknown, today: string): ChangelogCheck {
  const parsed = ChangelogSchema.safeParse(value);
  if (!parsed.success) {
    return {
      entries: [],
      problems: parsed.error.issues.map(
        (issue) => `${issue.path.map(String).join('.') || '(root)'}: ${issue.message}`,
      ),
    };
  }

  const problems: string[] = [];
  const seen = new Set<string>();
  parsed.data.forEach((entry, index) => {
    if (seen.has(entry.id)) problems.push(`${entry.id}: duplicate id`);
    seen.add(entry.id);
    if (entry.date > today) {
      problems.push(`${entry.id}: dated ${entry.date}, which is after today (${today})`);
    }
    const above = parsed.data[index - 1];
    if (above !== undefined && above.date < entry.date) {
      problems.push(
        `${entry.id}: dated ${entry.date}, newer than ${above.id} above it (newest first)`,
      );
    }
  });
  return { entries: parsed.data, problems };
}
